import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";

import { billingApi } from "@/api";
import { publicConfig } from "@/config/public";
import { countryForLocale } from "@/constants/countries";
import { PLUS_ANNUAL_PRICE, PLUS_MONTHLY_PRICE } from "@/constants/plans";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useBillingRegion } from "@/lib/billing-region";
import { formatAmount } from "@/lib/format-currency";
import type { SubscriptionPreview } from "@/types";
import i18n from "@/i18n";

import { getStripe } from "./stripe-js";
import type {
  SubscribeCheckoutProps,
  SubscribePlanId,
} from "./subscribe-checkout.types";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

/** Two-column layout kicks in here; below this the columns stack. */
const WIDE_BREAKPOINT = 900;

/** Dark palette background (constants/theme.ts) — what we match on to pick
 *  Stripe's night theme, since Elements has no notion of our theme. */
const DARK_BACKGROUND = "#0B1622";

/** Line height of a section label, pinned so the column alignment below is
 *  exact rather than at the mercy of the browser's default font metrics. */
const SECTION_LABEL_LINE_HEIGHT = 16;

/**
 * Drops the summary card to line up with the top of the plan cards rather than
 * the "Choose your plan" label above them: the label's own top margin, plus its
 * line, plus the column gap that follows it.
 */
const SUMMARY_TOP_OFFSET =
  Spacing.md + SECTION_LABEL_LINE_HEIGHT + Spacing.sm;

type PlanId = SubscribePlanId;

/**
 * A pricing result tagged with the inputs it was fetched for. Tax depends on
 * country, so a quote is only valid for the plan *and* country that produced it.
 */
type PricedPlan = { plan: PlanId; country: string } & (
  | { data: SubscriptionPreview; error?: undefined }
  | { error: string; data?: undefined }
);

/**
 * In-app opncue Plus checkout for web.
 *
 * The user never leaves opncue: we create the subscription `incomplete`
 * server-side, then Stripe Elements confirms the first payment inline. Card
 * details are entered in Stripe's iframe and never touch our code.
 *
 * Every price shown comes from the server's Stripe invoice preview, so the
 * summary can't drift from what actually gets charged.
 */
export function SubscribeCheckout({ initialPlan }: SubscribeCheckoutProps) {
  useTranslation();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;
  const isDark = c.background === DARK_BACKGROUND;

  // The server resolves the currency from where this request actually comes
  // from (£ UK / € Europe / $ elsewhere); the client only renders it. Until it
  // answers, "usd" — same number in every currency, so never a wrong amount.
  const region = useBillingRegion();
  const currency = region.currency.toLowerCase();
  const [plan, setPlan] = useState<PlanId>(initialPlan);
  const [country, setCountry] = useState(() => countryForLocale());
  const [priced, setPriced] = useState<PricedPlan | null>(null);

  // The billing-country selector starts on the locale guess, then snaps to the
  // server-geolocated country — unless the buyer has already chosen one, which
  // always wins.
  const countryTouched = useRef(false);
  useEffect(() => {
    if (region.country && !countryTouched.current) setCountry(region.country);
  }, [region.country]);
  const chooseCountry = useCallback((next: string) => {
    countryTouched.current = true;
    setCountry(next);
  }, []);

  // The result carries the inputs it priced, so a quote from the previous plan
  // or country can never be shown as if it priced the current one — no reset
  // needed when the user changes either.
  const current =
    priced?.plan === plan && priced?.country === country ? priced : null;
  const preview = current?.data ?? null;
  const previewError = current?.error ?? null;

  // Re-price whenever the plan or country changes. An in-flight request is
  // dropped on unmount or a fast re-toggle so a stale total can't land.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        // Country alone is enough to quote: prices are tax-inclusive, so the
        // total is the same whatever the tax rate works out to — only the
        // carved-out tax line moves. That's also why filling in the rest of
        // the address later never invalidates this quote.
        const data = await billingApi.previewSubscription(plan, { country });
        if (active) setPriced({ plan, country, data });
      } catch {
        if (active) {
          setPriced({
            plan,
            country,
            error: i18n.t("billing.checkoutPage.previewError"),
          });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [plan, country]);

  if (!publicConfig.stripe.publishableKey) {
    return (
      <Text style={styles.error}>{i18n.t("billing.card.notConfigured")}</Text>
    );
  }

  if (previewError) {
    return <Text style={styles.error}>{previewError}</Text>;
  }

  // The page mounts ONCE, whole. Elements does not wait for the server's
  // quote: the pinned plan prices are known here (they are the same number in
  // every currency and tax-inclusive), which is all deferred mode needs to
  // boot the payment iframe. The quote lands into an already-rendered page,
  // and what the buyer is charged never comes from this figure anyway — the
  // subscription is created and priced server-side on submit.
  const pinnedAmount = Math.round(
    (plan === "plus_annual" ? PLUS_ANNUAL_PRICE : PLUS_MONTHLY_PRICE) * 100,
  );

  return (
    <Elements
      stripe={getStripe()}
      options={{
        // Deferred mode: no subscription exists yet, so nothing is created for
        // people who open the page and leave. We create it on submit.
        //
        // With a free trial nothing is due today — the iframe collects a card
        // to SAVE (SetupIntent, confirmed by confirmSetup on submit), so
        // Elements runs in setup mode. react-stripe-js applies the switch via
        // elements.update() when the trial-priced quote lands.
        ...(preview?.trial
          ? { mode: "setup" as const }
          : {
              mode: "subscription" as const,
              amount: preview?.total ?? pinnedAmount,
            }),
        currency: preview?.currency ?? currency,
        appearance: {
          theme: isDark ? "night" : "stripe",
          variables: {
            colorPrimary: c.tint,
            colorBackground: c.surface,
            colorText: c.text,
            colorDanger: c.danger,
            borderRadius: `${Radius.sm}px`,
          },
          // The night theme raises inputs, tabs and blocks with box-shadows,
          // which read as halos against our flat dark surfaces.
          //
          // `:focus` is deliberately left alone and is more specific than these,
          // so keyboard users keep a visible focus ring. The selected tab loses
          // its shadow but stays obvious from its filled background.
          ...(isDark
            ? {
                rules: {
                  ".Input": { boxShadow: "none" },
                  ".Tab": { boxShadow: "none" },
                  ".Tab--selected": { boxShadow: "none" },
                  ".Block": { boxShadow: "none" },
                },
              }
            : {}),
        },
      }}
      // Deliberately NO key: react-stripe-js applies amount/currency changes
      // via elements.update(), in place. Keying on the total tore the whole
      // iframe down and re-booted it on every plan switch — the "checkout
      // loads three times" bug.
    >
      <CheckoutBody
        styles={styles}
        colors={c}
        isWide={isWide}
        plan={plan}
        onPlanChange={setPlan}
        country={country}
        onCountryChange={chooseCountry}
        preview={preview}
      />
    </Elements>
  );
}

function CheckoutBody({
  styles,
  colors,
  isWide,
  plan,
  onPlanChange,
  country,
  onCountryChange,
  preview,
}: {
  styles: ReturnType<typeof createStyles>;
  colors: ThemeColors;
  isWide: boolean;
  plan: PlanId;
  onPlanChange: (plan: PlanId) => void;
  country: string;
  onCountryChange: (country: string) => void;
  /** Null while the server is still pricing — the page renders without it. */
  preview: SubscriptionPreview | null;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();

  // Stripe collects and localizes name and address itself. It reports the
  // country and postcode back as the user types, which is all Stripe Tax needs
  // to price the order; the rest of the address lands on the payment method and
  // is copied onto the customer server-side once payment succeeds.
  const [postalCode, setPostalCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Height ratchet for the payment box. Stripe's iframe re-lays itself as its
  // lazy pieces arrive (Link's "save my information" most visibly, once a card
  // number is typed) and dips shorter for a frame while it does — the page
  // shrinks, the browser clamps the scroll position, and the user is shoved
  // upward. Holding the box at the tallest height it has reached means new
  // content only ever extends the page downward, off-screen, where scrolling
  // finds it — never a jump. Reset when the width changes (a reflow makes old
  // floors meaningless) or the buyer switches payment method, the one shrink
  // that is genuine.
  const [paymentFloor, setPaymentFloor] = useState({ width: 0, height: 0 });
  const paymentMethodType = useRef<string | null>(null);

  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Stripe validates its own fields in `elements.submit()`, so what is left to
  // check here is the recurring-charge consent — and that the server's quote
  // has arrived, since the consent line points at "the amount above".
  const canSubmit = !!stripe && !!elements && !!preview && agreed && !submitting;

  const billingAddress = useMemo(
    () => ({ country, ...(postalCode ? { postalCode } : {}) }),
    [country, postalCode],
  );

  const submit = useCallback(async () => {
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      // Validate the card fields before creating anything server-side.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? i18n.t("billing.card.setupFailed"));
        return;
      }

      // Country and postcode travel with the create call, which writes them to
      // the customer before charging so Stripe Tax bills against the location we
      // quoted. The full address is copied across from the payment method when
      // the payment is reconciled.
      const { subscriptionId, clientSecret, clientSecretType, status } =
        await billingApi.createSubscription(plan, billingAddress);

      const successUrl = `/checkout?status=success&kind=plus&subscription_id=${encodeURIComponent(subscriptionId)}`;

      // No secret means nothing to charge right now (trial / zero amount) — the
      // subscription is already live.
      if (!clientSecret) {
        if (status === "active" || status === "trialing") {
          router.replace(successUrl);
          return;
        }
        setError(i18n.t("billing.checkoutPage.startError"));
        return;
      }

      const confirmParams = {
        // No billing_details here — PaymentElement collects name and address
        // itself, localized per country, and attaches them to the payment
        // method.
        //
        // Used by payment methods that must leave the page (PayPal, Amazon
        // Pay, some 3DS flows). The return screen syncs by subscription id.
        return_url: `${window.location.origin}${successUrl}`,
      };
      // A trial's secret is a SetupIntent (save the card the first real
      // charge will use — nothing is due today); a paid start's is the first
      // invoice's PaymentIntent. The server says which it sent.
      const { error: confirmError } =
        clientSecretType === "setup"
          ? await stripe.confirmSetup({
              elements,
              clientSecret,
              confirmParams,
              redirect: "if_required",
            })
          : await stripe.confirmPayment({
              elements,
              clientSecret,
              confirmParams,
              redirect: "if_required",
            });

      if (confirmError) {
        setError(confirmError.message ?? i18n.t("billing.card.genericError"));
        return;
      }

      // Card confirms in-page rather than redirecting, so nothing navigates on
      // its own. Send it to the same return screen the redirect methods land on
      // — that screen verifies the payment server-side, refreshes the user and
      // runs the countdown home, so every payment method ends the same way.
      router.replace(successUrl);
    } catch {
      if (mounted.current) setError(i18n.t("billing.card.genericError"));
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }, [
    stripe,
    elements,
    submitting,
    billingAddress,
    plan,
    router,
  ]);

  return (
    <View style={[styles.columns, isWide ? styles.columnsWide : null]}>
      <View style={[styles.column, isWide ? styles.columnLeft : null]}>
        <Text style={styles.sectionLabel}>
          {i18n.t("billing.checkoutPage.planLabel")}
        </Text>
        <PlanChooser
          styles={styles}
          plan={plan}
          onPlanChange={onPlanChange}
          disabled={submitting}
        />

        <Text style={styles.sectionLabel}>
          {i18n.t("billing.checkoutPage.paymentLabel")}
        </Text>
        <View style={styles.card}>
          {/* Stripe owns the name and address fields so they're localized per
              country — the right labels, the right order, and postcode/state
              only where they actually apply. It reports country and postcode
              back through onChange, which is all Stripe Tax needs to price the
              order; the rest is copied onto the customer after payment.

              Stripe's own mandate line ("By subscribing, you authorise…") is
              suppressed: the summary's REQUIRED consent checkbox already says
              the same thing in our own words, and the duplicate was one of the
              late-appearing blocks that made the page load in stages. */}
          <View
            style={{ minHeight: paymentFloor.height }}
            onLayout={(event) => {
              const { width, height } = event.nativeEvent.layout;
              setPaymentFloor((prev) => {
                if (prev.width !== width) return { width, height };
                return height > prev.height ? { width, height } : prev;
              });
            }}
          >
            <PaymentElement
              options={{
                layout: "tabs",
                // Link is off. Its "Save my information" panel mounts only
                // after the card fields are complete, and Stripe then moves
                // focus into it — the browser scrolls the focused input into
                // view and the page lurches mid-entry. That focus steal
                // happens inside the iframe, where no layout of ours can
                // reach; a checkout that holds still is worth more than
                // Link's saved-card upsell. Restore by deleting this line.
                wallets: { link: "never" },
                terms: {
                  card: "never",
                  paypal: "never",
                  applePay: "never",
                  googlePay: "never",
                  cashapp: "never",
                },
              }}
              onChange={(event) => {
                // Switching method genuinely shrinks the form (PayPal is a
                // sentence; a card is a form) — release the floor for it.
                if (event.value.type !== paymentMethodType.current) {
                  paymentMethodType.current = event.value.type;
                  setPaymentFloor({ width: 0, height: 0 });
                }
                const address = event.value.billingDetails?.address;
                if (address?.country) onCountryChange(address.country);
                setPostalCode(address?.postalCode ?? "");
              }}
            />
          </View>
        </View>
      </View>

      <View style={[styles.column, isWide ? styles.columnRight : null]}>
        <SummaryPanel
          styles={styles}
          colors={colors}
          preview={preview}
          plan={plan}
          agreed={agreed}
          onAgreedChange={setAgreed}
          disabled={submitting}
          canSubmit={canSubmit}
          submitting={submitting}
          onSubmit={submit}
          error={error}
        />
      </View>
    </View>
  );
}

function PlanChooser({
  styles,
  plan,
  onPlanChange,
  disabled,
}: {
  styles: ReturnType<typeof createStyles>;
  plan: PlanId;
  onPlanChange: (plan: PlanId) => void;
  disabled: boolean;
}) {
  return (
    <View style={styles.planRow}>
      {(["plus_monthly", "plus_annual"] as const).map((option) => {
        const active = plan === option;
        return (
          <Pressable
            key={option}
            accessibilityRole="radio"
            accessibilityState={{ selected: active, disabled }}
            disabled={disabled}
            onPress={() => onPlanChange(option)}
            style={[
              styles.planCard,
              active && styles.planCardActive,
              disabled && styles.disabled,
            ]}
          >
            <View style={[styles.radio, active && styles.radioActive]}>
              {active ? <View style={styles.radioDot} /> : null}
            </View>
            <Text style={styles.planName}>
              {i18n.t(
                option === "plus_annual"
                  ? "billing.checkoutPage.planAnnual"
                  : "billing.checkoutPage.planMonthly",
              )}
            </Text>
            <Text style={styles.planNote}>
              {i18n.t(
                option === "plus_annual"
                  ? "billing.checkoutPage.billedYearly"
                  : "billing.checkoutPage.billedMonthly",
              )}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function SummaryPanel({
  styles,
  colors,
  preview,
  plan,
  agreed,
  onAgreedChange,
  disabled,
  canSubmit,
  submitting,
  onSubmit,
  error,
}: {
  styles: ReturnType<typeof createStyles>;
  colors: ThemeColors;
  preview: SubscriptionPreview | null;
  plan: PlanId;
  agreed: boolean;
  onAgreedChange: (value: boolean) => void;
  disabled: boolean;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
  error: string | null;
}) {
  const intervalLabel = i18n.t(
    plan === "plus_annual"
      ? "billing.checkoutPage.perYear"
      : "billing.checkoutPage.perMonth",
  );

  return (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryTitle}>
        {i18n.t("billing.checkoutPage.summaryTitle")}
      </Text>

      {!preview ? (
        // The quote is still on its way. The page around it is fully live —
        // only these figures wait, in a block sized like the rows it becomes,
        // so their arrival doesn't shove the consent and button around.
        <View style={styles.summaryLoading}>
          <ActivityIndicator size="small" color={colors.tint} />
        </View>
      ) : null}

      {preview?.lines.map((line, index) => (
        <View key={`${line.label}-${index}`} style={styles.summaryRow}>
          <Text style={styles.summaryLabel} numberOfLines={2}>
            {line.label}
          </Text>
          <Text style={styles.summaryValue}>
            {formatAmount(line.amount, preview.currency)}
          </Text>
        </View>
      ))}

      {/* Subtotal and tax only make sense as separate rows when tax is added on
          top. Inclusive tax is already inside the total, so we say so in the
          total's label instead of double-counting it. */}
      {preview && preview.tax > 0 && !preview.taxInclusive ? (
        <>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              {i18n.t("billing.checkoutPage.subtotal")}
            </Text>
            <Text style={styles.summaryValue}>
              {formatAmount(preview.subtotal, preview.currency)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              {i18n.t("billing.checkoutPage.tax")}
            </Text>
            <Text style={styles.summaryValue}>
              {formatAmount(preview.tax, preview.currency)}
            </Text>
          </View>
        </>
      ) : null}

      {preview ? (
        <>
          <View style={styles.divider} />

          <View style={styles.summaryRow}>
            <Text style={styles.totalLabel}>
              {i18n.t(
                preview.trial
                  ? // The trial invoice IS the £0 — "Total due today: £0.00"
                    // comes straight from Stripe's preview, same as any total.
                    "billing.checkoutPage.total"
                  : preview.taxInclusive && preview.tax > 0
                    ? "billing.checkoutPage.totalInclTax"
                    : "billing.checkoutPage.total",
              )}
            </Text>
            <Text style={styles.totalValue}>
              {formatAmount(preview.total, preview.currency)}
            </Text>
          </View>

          <Text style={styles.renewNote}>
            {preview.trial
              ? i18n.t("billing.trial.thenNote", {
                  price: formatAmount(preview.trial.amount, preview.currency),
                  interval: intervalLabel,
                  date: preview.trial.firstChargeAt
                    ? new Date(preview.trial.firstChargeAt).toLocaleDateString(
                        undefined,
                        { day: "numeric", month: "short", year: "numeric" },
                      )
                    : "",
                })
              : i18n.t("billing.checkoutPage.renewNote", {
                  price: formatAmount(preview.total, preview.currency),
                  interval: intervalLabel,
                })}
          </Text>
        </>
      ) : null}

      <Pressable
        accessibilityRole="checkbox"
        accessibilityState={{ checked: agreed, disabled }}
        disabled={disabled}
        onPress={() => onAgreedChange(!agreed)}
        style={styles.consentRow}
      >
        <View style={[styles.checkbox, agreed && styles.checkboxChecked]}>
          {agreed ? <Text style={styles.checkboxMark}>✓</Text> : null}
        </View>
        <Text style={styles.consentText}>
          {i18n.t("billing.checkoutPage.consent")}
        </Text>
      </Pressable>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !canSubmit }}
        disabled={!canSubmit}
        onPress={onSubmit}
        style={({ pressed }) => [
          styles.primaryBtn,
          !canSubmit && styles.disabled,
          pressed && styles.pressed,
        ]}
      >
        {submitting ? (
          <ActivityIndicator size="small" color={colors.onTint} />
        ) : (
          <Text style={styles.primaryBtnText}>
            {i18n.t(
              preview?.trial
                ? "billing.trial.startCta"
                : "billing.checkoutPage.subscribe",
            )}
          </Text>
        )}
      </Pressable>
    </View>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    columns: { gap: Spacing.xl, width: "100%" },
    columnsWide: { flexDirection: "row", alignItems: "flex-start" },
    column: { gap: Spacing.sm },
    columnLeft: { flex: 1.3 },
    // Only applied in the two-column layout, so the offset can't leave a gap
    // above the summary once the columns stack.
    columnRight: { flex: 1, marginTop: SUMMARY_TOP_OFFSET },

    sectionLabel: {
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: SECTION_LABEL_LINE_HEIGHT,
      fontWeight: "700",
      letterSpacing: 0.5,
      textTransform: "uppercase",
      color: c.textMuted,
      marginTop: Spacing.md,
    },
    card: {
      backgroundColor: c.surface,
      borderRadius: Radius.md,
      borderWidth: 1,
      borderColor: c.border,
      padding: Spacing.lg,
      gap: Spacing.sm,
    },

    planRow: { flexDirection: "row", gap: Spacing.md },
    planCard: {
      flex: 1,
      backgroundColor: c.surface,
      borderRadius: Radius.md,
      borderWidth: 1,
      borderColor: c.border,
      padding: Spacing.lg,
      gap: Spacing.xs,
    },
    planCardActive: {
      borderColor: c.tint,
      backgroundColor: c.backgroundSelected,
    },
    radio: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: c.border,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: Spacing.xs,
    },
    radioActive: { borderColor: c.tint },
    radioDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: c.tint,
    },
    planName: {
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: "700",
      color: c.text,
    },
    planNote: {
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      color: c.textSecondary,
    },

    fieldLabel: {
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "600",
      color: c.textSecondary,
    },
    input: {
      minHeight: 44,
      borderRadius: Radius.sm,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.backgroundElement,
      paddingHorizontal: Spacing.md,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      color: c.text,
    },
    fieldRow: { flexDirection: "row", gap: Spacing.md },
    fieldHalf: { flex: 1, gap: Spacing.sm },
    select: {
      minHeight: 44,
      borderRadius: Radius.sm,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.backgroundElement,
      paddingHorizontal: Spacing.md,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      color: c.text,
      width: "100%",
    },

    summaryCard: {
      backgroundColor: c.surfaceAlt,
      borderRadius: Radius.lg,
      borderWidth: 1,
      borderColor: c.border,
      padding: Spacing.xl,
      gap: Spacing.md,
    },
    summaryTitle: {
      fontFamily: FONT_FAMILY,
      fontSize: 20,
      fontWeight: "800",
      color: c.text,
      marginBottom: Spacing.xs,
    },
    summaryLoading: { paddingVertical: Spacing.xl },
    summaryRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: Spacing.md,
    },
    summaryLabel: {
      flex: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      color: c.textSecondary,
    },
    summaryValue: {
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      color: c.textSecondary,
    },
    divider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: c.border,
      marginVertical: Spacing.xs,
    },
    totalLabel: {
      flex: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: "800",
      color: c.text,
    },
    totalValue: {
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: "800",
      color: c.text,
    },
    renewNote: {
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
      color: c.textSecondary,
      backgroundColor: c.backgroundElement,
      borderRadius: Radius.sm,
      padding: Spacing.md,
    },

    consentRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: Spacing.sm,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: Radius.sm / 2,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surface,
      alignItems: "center",
      justifyContent: "center",
      marginTop: 2,
    },
    checkboxChecked: { backgroundColor: c.tint, borderColor: c.tint },
    checkboxMark: {
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "800",
      color: c.onTint,
    },
    consentText: {
      flex: 1,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
      color: c.textSecondary,
    },

    primaryBtn: {
      minHeight: 48,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.lg,
      backgroundColor: c.tint,
      marginTop: Spacing.xs,
    },
    primaryBtnText: {
      fontFamily: FONT_FAMILY,
      color: c.onTint,
      fontSize: 16,
      fontWeight: "800",
    },

    error: { color: c.danger, fontSize: 14, marginTop: Spacing.xs },
    disabled: { opacity: 0.6 },
    pressed: { opacity: 0.85 },
  });
}
